<template>
    <div class="w-full">
        <Listbox v-model="selectedLanguageId">
            <ListboxLabel class="block text-sm font-medium text-slate-600 text-center">
                {{ $t('login.change_language') }}
            </ListboxLabel>
            <div class="relative mt-1">
                <ListboxButton class="inline-block w-full rounded-md shadow-sm">
                    <span class="inline-block cursor-default relative w-full rounded-md border border-slate-200 bg-white pl-3 pr-10 py-1.5 text-left transition ease-in-out duration-150 sm:text-sm sm:leading-5">
                        <span class="flex items-center space-x-3">
                              <span :class="`fi fi-${selectedLanguage.flagCode} fis rounded-full`" style="height: 20px; width: 20px;"></span>
                            <span class="block truncate text-slate-500">{{ selectedLanguage.name }}</span>
                        </span>
                        <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                            <svg class="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path></svg>
                        </span>
                    </span>
                </ListboxButton>

                <Transition
                    enter-active-class="transition duration-100 ease-out"
                    enter-from-class="transform opacity-0 -translate-y-1"
                    enter-to-class="transform opacity-100 translate-y-0"
                    leave-active-class="transition duration-75 ease-in"
                    leave-from-class="transform opacity-100 translate-y-0"
                    leave-to-class="transform opacity-0 -translate-y-1"
                >

                    <ListboxOptions
                        class="absolute bottom-full mb-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm"
                    >

                        <ListboxOption
                            v-for="language in languages"
                            :key="language.id"
                            :value="language.id"
                            v-slot="{ active, selected }"
                        >
                            <li
                                :class="[
                                    active ? 'bg-indigo-50 text-slate-700' : 'text-gray-900', 'rounded-lg relative cursor-default select-none py-2 pl-4 pr-4']"
                            >
                                <span class="flex items-center space-x-3">
                                   <span :class="`fi fi-${language.flagCode} fis rounded-full`" style="height: 20px; width: 20px;"></span>
                                    <span :class="{ 'font-semibold': selected, 'font-normal': !selected }" class="block truncate">{{ language.name }}</span>
                                </span>
                                <span v-if="selected" class="absolute inset-y-0 right-0 flex items-center pr-4">
                                    <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path></svg>
                                </span>
                            </li>
                        </ListboxOption>
                    </ListboxOptions>
                </Transition>
            </div>
        </Listbox>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useLanguage } from '@/Composables/useLanguage';
import { Listbox, ListboxLabel, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue';
import "/node_modules/flag-icons/css/flag-icons.min.css";

import { en } from 'yup-locales';
import { setLocale } from 'yup';

const { languages, changeLanguage, locale } = useLanguage();
const selectedLanguageId = ref(localStorage.getItem('userLanguage') || locale.value);

// Function to load and apply Yup locale dynamically
const loadAndApplyYupLocale = async (langId) => {
    try {
        if (langId === 'sl') {
            const slovenianLocaleModule = await import("../Translations/yup.js");
            slovenianLocaleModule.yupTranslations();
        } else {
            setLocale(en);
        }
    } catch (error) {
        console.error(`Failed to load Yup locale for '${langId}':`, error);
    }
};

watch(locale, (newLocale) => {
    const language = languages.value.find(lang => lang.locale === newLocale);
    if (language && language.id !== selectedLanguageId.value) {
        selectedLanguageId.value = language.id;
    }
});

// Set the selected language based on the URL query parameter or local storage
onMounted(() => {
    const queryParams = new URLSearchParams(window.location.search);
    let langParam = queryParams.get('lang');

    if (langParam && languages.value.some(lang => lang.id === langParam)) {
        selectedLanguageId.value = langParam;
        return;
    }

    // Check if the new language is different from the stored user language
    const storedLanguage = localStorage.getItem('userLanguage');
    if (storedLanguage && storedLanguage !== selectedLanguageId.value) {
        selectedLanguageId.value = storedLanguage;
    }
});

// Watch the selected language and update as necessary
watch(selectedLanguageId, (newVal) => {
    changeLanguage(newVal);
    loadAndApplyYupLocale(newVal);
    localStorage.setItem('userLanguage', newVal);
});

// Computed property to show the selected language's details
const selectedLanguage = computed(() => {
    return languages.value.find(language => language.id === selectedLanguageId.value) || languages.value[0];
});
</script>
